import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Save, Upload, X } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['blockquote', 'link'],
    [{ 'color': [] }],
    ['clean']
  ]
};

const AdminPages = () => {
  const [pageData, setPageData] = useState({
    heading: "Driving India's Green Energy Transformation",
    description: '',
    who_we_are_title: 'Who We Are',
    who_we_are_text: '',
    image_url: '',
    value_1_title: 'Our Mission',
    value_1_description: '',
    value_2_title: 'Innovation',
    value_2_description: '',
    value_3_title: 'Customer Focus',
    value_3_description: '',
    value_4_title: 'Excellence',
    value_4_description: '',
    stat_1_value: '15+',
    stat_1_label: 'Years of Experience',
    stat_2_value: '500+',
    stat_2_label: 'MW Installed Capacity',
    stat_3_value: '100%',
    stat_3_label: 'Client Satisfaction',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchPageContent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/pages/about`);
      if (data.content && Object.keys(data.content).length > 0) {
        setPageData(prev => ({ ...prev, ...data.content }));
      }
    } catch (error) {
      console.error('Failed to load page content:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPageContent();
  }, [fetchPageContent]);

  const handleChange = (field, value) => {
    setPageData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleChange('image_url', data.url);
    } catch (error) {
      setError('Image upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/api/pages/about`, {
        page_name: 'about',
        content: pageData
      }, { withCredentials: true });
      setSuccess('About Us page updated successfully!');
      setTimeout(() => setSuccess(''), 4000);
    } catch (error) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-pages">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-primary mb-2" data-testid="pages-title">Edit About Us Page</h1>
          <p className="text-text_muted">Update the content shown on the public About Us page</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50"
          data-testid="save-page-button"
        >
          <Save size={20} />
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded text-green-700" data-testid="save-success">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700" data-testid="save-error">
          {error}
        </div>
      )}

      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-white p-6 rounded-sm border border-border">
          <h2 className="text-lg font-semibold text-primary mb-4">Hero Section</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Page Heading</label>
              <input
                type="text"
                value={pageData.heading}
                onChange={(e) => handleChange('heading', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="input-heading"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Page Description</label>
              <textarea
                value={pageData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows="3"
                className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                data-testid="input-description"
              />
            </div>
          </div>
        </div>

        {/* Who We Are Section */}
        <div className="bg-white p-6 rounded-sm border border-border">
          <h2 className="text-lg font-semibold text-primary mb-4">Who We Are Section</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Section Title</label>
              <input
                type="text"
                value={pageData.who_we_are_title}
                onChange={(e) => handleChange('who_we_are_title', e.target.value)}
                className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="input-who-title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Content</label>
              <ReactQuill
                theme="snow"
                value={pageData.who_we_are_text}
                onChange={(val) => handleChange('who_we_are_text', val)}
                modules={QUILL_MODULES}
                style={{ minHeight: '150px' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Section Image</label>
              {pageData.image_url && (
                <div className="relative w-full h-40 rounded overflow-hidden border border-border mb-3">
                  <img src={getImageSrc(pageData.image_url)} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => handleChange('image_url', '')}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded font-medium hover:bg-opacity-90 transition-all disabled:opacity-50"
                  data-testid="upload-about-image"
                >
                  <Upload size={16} />
                  <span>{uploading ? 'Uploading...' : 'Upload Image'}</span>
                </button>
                <span className="text-xs text-text_muted self-center">or paste URL below</span>
              </div>
              <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageUpload} />
              <input
                type="text"
                value={pageData.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                placeholder="Or paste image URL..."
                className="w-full mt-2 px-4 py-2 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                data-testid="input-about-image-url"
              />
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="bg-white p-6 rounded-sm border border-border">
          <h2 className="text-lg font-semibold text-primary mb-4">Core Values (4 cards)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="border border-border rounded p-4">
                <label className="block text-sm font-medium text-primary mb-2">Value {n} — Title</label>
                <input
                  type="text"
                  value={pageData[`value_${n}_title`]}
                  onChange={(e) => handleChange(`value_${n}_title`, e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary mb-3"
                  data-testid={`input-value-${n}-title`}
                />
                <label className="block text-sm font-medium text-primary mb-2">Value {n} — Description</label>
                <textarea
                  value={pageData[`value_${n}_description`]}
                  onChange={(e) => handleChange(`value_${n}_description`, e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                  data-testid={`input-value-${n}-desc`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white p-6 rounded-sm border border-border">
          <h2 className="text-lg font-semibold text-primary mb-4">Why Choose Us — Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="border border-border rounded p-4">
                <label className="block text-sm font-medium text-primary mb-2">Stat {n} — Value</label>
                <input
                  type="text"
                  value={pageData[`stat_${n}_value`]}
                  onChange={(e) => handleChange(`stat_${n}_value`, e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary mb-3"
                  data-testid={`input-stat-${n}-value`}
                />
                <label className="block text-sm font-medium text-primary mb-2">Stat {n} — Label</label>
                <input
                  type="text"
                  value={pageData[`stat_${n}_label`]}
                  onChange={(e) => handleChange(`stat_${n}_label`, e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid={`input-stat-${n}-label`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Save */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-8 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50"
            data-testid="save-page-button-bottom"
          >
            <Save size={20} />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPages;
